<script setup>
import { FileZipIcon, DownloadIcon, MaximizeIcon } from 'vue-tabler-icons';
import { markRaw, onMounted, onUnmounted, ref, toRaw, defineProps, defineEmits } from 'vue';
import { useStore } from 'vuex';
import { forEachAsync } from '@/utils/array';
import { fetchResources, fetchResourceById } from '@/connectors/ras';
import {
  Canvas,
  TypeComponent,
  Entity,
  LoadingScreenComponent,
  LocalFilesManager,
  Material,
  Texture,
  EnvironmentComponent,
  OrbitCameraComponent,
  // MeshPropertiesComponent,
} from 'atlatl-visual-runtime';

const props = defineProps({
  draft: { type: Object, default: null },
  model: { type: Object, default: null },
  material: { type: Object, default: null },
});

const emit = defineEmits(['update:canvas']);

const store = useStore();
const renderCanvas = ref(null);
const previewerContainerEl = ref(null);

const loading = ref(false);

async function createTextures(textures, { canvas }) {
  const {
    data: { objects: textureResources },
  } = await fetchResources(
    { ids: Object.keys(textures) },
    { resourceType: 'texture', request: store.state.users.user.session.request },
  );

  const {
    data: { objects: textureFileResources },
  } = await fetchResources(
    { ids: textureResources.map(({ data: { textureFile } }) => textureFile) },
    { resourceType: 'textureFile', request: store.state.users.user.session.request },
  );

  await forEachAsync(textureResources, async (textureResource) => {
    const texture = new Texture(
      canvas,
      textureFileResources.find(({ shortId }) => shortId === textureResource.data.textureFile).files[0].url,
      textureResource.data,
    );
    await texture.ready();
    textures[textureResource.shortId].dependentMaterials.forEach(({ material, channel }) => {
      material.setChannel(texture, channel);
    });
  });
}

async function createMaterials(materials, { canvas }) {
  const {
    data: { objects: materialResources },
  } = await fetchResources(
    { names: materials.map(({ name }) => name) },
    { resourceType: 'material', request: store.state.users.user.session.request },
  );

  const textures = {};

  await forEachAsync(materials, async (material) => {
    const resource = materialResources.find(({ name }) => name === material.name);
    const materialObj = new Material(canvas, { name: resource.name, ...resource.data });
    resource.data.textures.forEach(({ id, channel }) => {
      if (!textures[id]) {
        textures[id] = { dependentMaterials: [] };
      }
      textures[id].dependentMaterials.push({ material: materialObj, channel });
    });
  });

  await createTextures(textures, { canvas });
}

async function registerModel({ files: [gltfResource, buffersResource] }) {
  const gltfJSON = await fetch(gltfResource.url).then((res) => res.json());
  const gltfFile = new File(
    [new Blob([JSON.stringify(gltfJSON)], { type: 'application/json' })],
    gltfResource.name.toLowerCase(),
    { contentType: 'model/gltf+json' },
  );

  const buffersBlob = await fetch(buffersResource.url).then((res) => res.blob());
  const buffersFile = new File([buffersBlob], buffersResource.name.toLowerCase(), {
    contentType: 'application/octet-stream',
  });

  LocalFilesManager.addLocalFiles([
    { name: buffersResource.name.toLowerCase(), file: buffersFile },
    { name: gltfResource.name.toLowerCase(), file: gltfFile },
  ]);

  return { name: gltfResource.name, gltf: gltfJSON };
}

const canvas = ref();
let entityType;
let loadingScreen;

onMounted(async () => {
  const renderEl = previewerContainerEl.value.querySelector('canvas');
  canvas.value = markRaw(new Canvas(renderEl));
  loadingScreen = new LoadingScreenComponent(canvas.value, {
    onShow: () => {
      loading.value = true;
    },
    onHide: () => {
      loading.value = false;
    },
  });

  loadingScreen.show();

  try {
    if (props.material) {
      const environment = new EnvironmentComponent(canvas.value);
      environment.enableGround(true);

      const materialObj = new Material(canvas.value, { name: props.material.name, ...toRaw(props.material.data) });

      const textures = {};
      props.material.data.textures.forEach(({ id, channel }) => {
        if (!textures[id]) {
          textures[id] = { dependentMaterials: [] };
        }
        textures[id].dependentMaterials.push({ material: materialObj, channel });
      });
      await createTextures(textures, { canvas: canvas.value });

      environment.groundMaterial = materialObj;

      const camera = canvas.value.getComponentByType(OrbitCameraComponent);
      camera.upperRadiusLimit = 20;
      camera.radius = 20;
      camera.beta = 0;
      camera.alpha = 90;

      // await entityType?.destroy();
      // entityType = await TypeComponent.createSphere(canvas.value);
      // const entity = new Entity(props.material.id, entityType);

      // const meshComponent = new MeshPropertiesComponent(entity);

      // meshComponent.setMaterial([...entity.getNodes()][0].name, materialObj.id);
    } else {
      const modelResource =
        props.model ||
        (await fetchResourceById(
          { id: props.draft.models[0] },
          { resourceType: 'mesh', request: store.state.users.user.session.request },
        ).then((res) => res.data));

      const { name: modelName, gltf } = await registerModel(modelResource);

      await createMaterials(gltf.materials, { canvas: canvas.value });

      await entityType?.destroy();
      entityType = await TypeComponent.createImportedType(canvas.value, { url: `file:${modelName}` });

      // eslint-disable-next-line no-new
      new Entity(modelResource.id, entityType);
    }

    emit('update:canvas', canvas.value);
  } finally {
    loadingScreen.hide();
  }
});

onUnmounted(async () => {
  await canvas.value?.destroy();
});

function fullscreen() {
  if (document.fullscreenElement === previewerContainerEl.value) {
    return document.exitFullscreen();
  }
  return previewerContainerEl.value.requestFullscreen();
}

async function downloadAsGLTF() {
  loadingScreen.show();
  const exported = await canvas.value.exportGLTF();

  const { default: JSZip } = await import('jszip');
  const zip = new JSZip();
  const gltf = JSON.parse(exported.gltf.value);
  gltf.images = (gltf.images || []).map(({ uri, ...image }) => ({
    uri: `${uri.replace(/\?.*/, '')}.${uri.split('.').pop()}`,
    ...image,
  }));

  zip.file(exported.gltf.name, new Blob([JSON.stringify(gltf)], { type: 'application/octet-stream' }));

  exported.meshes.forEach(({ name, value }) => zip.file(name, value, { binary: true }));

  exported.textures.forEach(({ name, value }) =>
    zip.file(`${name.replace(/\?.*/, '')}.${name.split('.').pop()}`, value, { binary: true }),
  );
  const zipBlob = await zip.generateAsync({ type: 'blob' });
  const url = URL.createObjectURL(zipBlob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${exported.gltf.name}.zip`;
  a.click();
  URL.revokeObjectURL(url);

  a.remove();
  loadingScreen.hide();
}

async function downloadAsGLB() {
  loadingScreen.show();
  const a = document.createElement('a');

  const exported = await canvas.value.exportGLBAsBlob();
  const url = URL.createObjectURL(exported);
  a.href = url;
  a.download = 'asset.glb';
  a.click();
  URL.revokeObjectURL(url);

  a.remove();
  loadingScreen.hide();
}
</script>

<template>
  <div ref="previewerContainerEl" class="previewer-container">
    <canvas ref="renderCanvas" class="w-full" style="aspect-ratio: 16 / 9" @readystatechange="load"></canvas>
    <div v-show="loading" class="previewer__loader">
      <img src="https://atlatl-visual.s3.amazonaws.com/images/defaultlogov2.gif" class="w-48 animate-bounce" />
    </div>
    <div class="previewer__fullscreen" @click="fullscreen"><maximize-icon /></div>

    <div class="absolute bottom-3 left-3 flex gap-2">
      <button class="previewer__download left-3" @click.prevent="downloadAsGLTF">
        <file-zip-icon></file-zip-icon>GLTF
      </button>
      <button class="previewer__download left-16" @click.prevent="downloadAsGLB">
        <download-icon></download-icon>GLB
      </button>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.previewer-container {
  @apply relative mx-auto flex max-w-6xl items-center justify-center p-1;
}
.previewer__loader {
  @apply absolute top-0 left-0 z-30 flex h-full w-full items-center justify-center rounded bg-cover;
  background-image: url(https://atlatl-visual.s3.amazonaws.com/images/defaultsplashv2.png);
}
.previewer__fullscreen {
  @apply absolute top-3 right-3 z-20 flex h-8 w-8 items-center justify-center rounded-2xl bg-white bg-opacity-70;
}
.previewer__download {
  @apply z-20 grid h-8 w-8 overflow-hidden rounded-2xl bg-white bg-opacity-70 p-1 text-left duration-100;
  grid-template-columns: 1.75rem auto;
}
.previewer__download:hover {
  @apply bottom-16 w-20;
}
</style>
